import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../css/personality.css";
import { useToken } from "./TokenContext";

const Personality = () => {
  const navigate = useNavigate();
  const { userRole, loggedInUser, loggedInToken, locationNames, isLoading } = useToken();

  const [results, setResults] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [satelliteId, setSatelliteId] = useState(null);
  const [companyList, setCompanyList] = useState([]);
  const [satelliteList, setSatelliteList] = useState([]);
  const [userSatelliteList, setUserSatelliteList] = useState([]); // ロール4用の所属拠点リスト
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ ロールによる閲覧制限
  useEffect(() => {
    // ローディング中はスキップ
    if (isLoading) {
      return;
    }

    // userRoleがnullやundefinedの場合は、localStorageから取得を試みる
    let role = userRole;
    if (role === null || role === undefined) {
      const savedRole = localStorage.getItem("userRole");
      if (savedRole) {
        role = Number(savedRole);
      }
    }

    // デバッグログ
    console.log("[Personality] 権限チェック:", {
      userRole,
      role,
      isLoading,
      savedRole: localStorage.getItem("userRole")
    });

    // ロールが取得できない、またはロール4未満の場合はリダイレクト
    if (role === null || role === undefined || isNaN(role) || role < 4) {
      console.warn("[Personality] 権限不足:", { role, userRole });
      alert("このページは指導員以上のみ閲覧可能です");
      navigate("/");
      return;
    }

    console.log("[Personality] 権限チェック通過:", { role });
  }, [userRole, isLoading, navigate]);

  // ✅ ロール9用の企業リスト取得
  useEffect(() => {
    const fetchCompanies = async () => {
      if (userRole === 9) {
        if (!loggedInToken) {
          console.warn("[Personality] トークンが未設定のため、企業リストを取得できません");
          setError("ログイントークンが設定されていません");
          return;
        }

        try {
          console.log("[Personality] 企業リスト取得開始");
          const res = await axios.post("https://mpzzlvohx7.execute-api.ap-northeast-3.amazonaws.com/prod/api/personality", {
            action: "list-companies",
            token: loggedInToken,
          });
          
          console.log("[Personality] 企業リスト取得レスポンス:", res.data);
          
          // レスポンス形式の確認
          let companies = [];
          if (res.data.success !== false && res.data.companies) {
            companies = res.data.companies;
          } else if (Array.isArray(res.data)) {
            companies = res.data;
          } else if (res.data.companies) {
            companies = res.data.companies;
          }

          if (!Array.isArray(companies)) {
            console.warn("[Personality] 企業リストの形式が不正です:", res.data);
            companies = [];
          }

          setCompanyList(companies);
          setError(""); // エラーをクリア
          
          // 最初の企業を選択
          if (companies.length > 0 && !companyId) {
            setCompanyId(companies[0].id);
          } else if (companies.length === 0) {
            console.warn("[Personality] 企業リストが空です");
            setError("企業が見つかりませんでした");
          }
        } catch (err) {
          console.error("[Personality] 企業リストの取得に失敗しました", {
            message: err.message,
            response: err.response?.data,
            status: err.response?.status,
            statusText: err.response?.statusText
          });
          
          let errorMessage = "企業リストの取得に失敗しました";
          if (err.response?.data?.message) {
            errorMessage = err.response.data.message;
          } else if (err.message) {
            errorMessage = `${errorMessage}: ${err.message}`;
          }
          
          setError(errorMessage);
          setCompanyList([]); // エラー時は空配列を設定
        }
      }
    };
    fetchCompanies();
  }, [userRole, loggedInToken, companyId]);

  // ✅ ロール9用の拠点リスト取得（企業選択時）
  useEffect(() => {
    const fetchSatellites = async () => {
      if (userRole === 9 && companyId) {
        try {
          const res = await axios.post("https://mpzzlvohx7.execute-api.ap-northeast-3.amazonaws.com/prod/api/personality", {
            action: "list-satellites",
            token: loggedInToken,
            companyId: companyId,
          });
          const satellites = res.data.satellites || [];
          setSatelliteList(satellites);
          // 最初の拠点を選択
          if (satellites.length > 0 && !satelliteId) {
            setSatelliteId(satellites[0].id);
          } else if (satellites.length === 0) {
            setSatelliteId(null);
          }
        } catch (err) {
          console.error("拠点リストの取得に失敗しました", err);
          setSatelliteList([]);
          setSatelliteId(null);
        }
      } else if (userRole === 9 && !companyId) {
        setSatelliteList([]);
        setSatelliteId(null);
      }
    };
    fetchSatellites();
  }, [userRole, loggedInToken, companyId]);

  // ✅ ロール4用の所属拠点リスト取得
  useEffect(() => {
    const fetchUserSatellites = async () => {
      if (userRole === 4 && locationNames && Array.isArray(locationNames) && locationNames.length > 0) {
        try {
          // 拠点名から拠点IDを取得するAPIを呼び出す
          const res = await axios.post("https://mpzzlvohx7.execute-api.ap-northeast-3.amazonaws.com/prod/api/personality", {
            action: "get-user-satellites",
            token: loggedInToken,
            locationNames: locationNames,
          });
          const satellites = res.data.satellites || [];
          setUserSatelliteList(satellites);
          // 最初の拠点を選択
          if (satellites.length > 0 && !satelliteId) {
            setSatelliteId(satellites[0].id);
          }
        } catch (err) {
          console.error("所属拠点リストの取得に失敗しました", err);
          // エラー時はlocationNamesから簡易的なリストを作成
          const satellites = locationNames.map((name, index) => ({
            id: `satellite-${index}`,
            name: name,
          }));
          setUserSatelliteList(satellites);
          if (satellites.length > 0 && !satelliteId) {
            setSatelliteId(satellites[0].id);
          }
        }
      } else if (userRole === 4) {
        // 拠点が1つの場合はそのまま使用
        setUserSatelliteList([]);
        setSatelliteId(null);
      }
    };
    fetchUserSatellites();
  }, [userRole, loggedInToken, locationNames]);

  const handleFetch = async () => {
    setLoading(true);
    try {
      // ロール9の場合は選択した拠点ID、ロール4の場合は選択した拠点ID、それ以外はloggedInUserを使用
      const targetId = userRole === 9 
        ? (satelliteId || companyId) 
        : userRole === 4 
        ? (satelliteId || loggedInUser)
        : loggedInUser;

      const res = await axios.post("https://mpzzlvohx7.execute-api.ap-northeast-3.amazonaws.com/prod/api/personality", {
        action: "fetch-admin-results",
        token: loggedInToken,
        companyId: userRole === 9 ? companyId : undefined,
        satelliteId: satelliteId || undefined,
        targetId: targetId,
      });

      if (!res.data || !Array.isArray(res.data.results)) {
        throw new Error("無効なデータ形式");
      }

      const files = res.data.results.map((entry) => ({
        name: entry.name,
        updated: new Date(entry.updated),
        signedUrls: entry.signedUrls || {},
      }));

      setResults(files);
      setError("");
    } catch (e) {
      console.error(e);
      setError("ファイル取得に失敗しました");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedResults = [...results].sort((a, b) => {
    const aValue = sortConfig.key === "updated" ? a.updated : a[sortConfig.key].toLowerCase();
    const bValue = sortConfig.key === "updated" ? b.updated : b[sortConfig.key].toLowerCase();
    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const filteredResults = sortedResults.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLogout = () => {
    if (window.confirm("ログアウトしてもよろしいですか？")) {
      localStorage.clear();
      navigate("/");
    }
  };

  return (
    <div className="personality-container">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1em" }}>
        <h1 style={{ margin: 0 }}>結果一覧（管理者用）</h1>
        <button 
          onClick={handleLogout} 
          style={{
            padding: "0.5em 1em",
            backgroundColor: "#f44336",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "bold"
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = "#d32f2f"}
          onMouseOut={(e) => e.target.style.backgroundColor = "#f44336"}
        >
          ログアウト
        </button>
      </div>

      {/* ロール9: 企業と拠点を選択 */}
      {userRole === 9 && (
        <>
          {error && (
            <div style={{ 
              marginBottom: "1em", 
              padding: "0.75em", 
              backgroundColor: "#ffebee", 
              border: "1px solid #f44336", 
              borderRadius: "4px",
              color: "#c62828"
            }}>
              <strong>エラー:</strong> {error}
            </div>
          )}
          <div style={{ marginBottom: "1em", display: "flex", gap: "1em", flexWrap: "wrap" }}>
            <div>
              <label htmlFor="company-select">企業を選択：</label>
              <select
                id="company-select"
                value={companyId || ""}
                onChange={(e) => {
                  setCompanyId(e.target.value);
                  setSatelliteId(null); // 企業変更時は拠点をリセット
                }}
                disabled={companyList.length === 0}
              >
                <option value="">企業を選択してください</option>
                {companyList.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            {companyId && (
              <div>
                <label htmlFor="satellite-select">拠点を選択：</label>
                <select
                  id="satellite-select"
                  value={satelliteId || ""}
                  onChange={(e) => setSatelliteId(e.target.value)}
                >
                  <option value="">拠点を選択してください</option>
                  {satelliteList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </>
      )}

      {/* ロール4: 所属拠点を選択（複数所属の場合のみ） */}
      {userRole === 4 && userSatelliteList.length > 1 && (
        <div style={{ marginBottom: "1em" }}>
          <label htmlFor="satellite-select">所属拠点を選択：</label>
          <select
            id="satellite-select"
            value={satelliteId || ""}
            onChange={(e) => setSatelliteId(e.target.value)}
          >
            <option value="">拠点を選択してください</option>
            {userSatelliteList.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="token-check">
        <button onClick={handleFetch}>データ取得</button>
      </div>

      {results.length > 0 && (
        <>
          <div className="advice-link-wrapper">
            <a
              href="https://chatgpt.com/g/g-67f3439eac34819194ded801e379ee59-zhi-ye-shi-xing-zhen-duan-atohaisa-gpt"
              target="_blank"
              rel="noopener noreferrer"
              className="advice-link-button"
            >
              支援員向けアドバイスページへ
            </a>
          </div>
          <div style={{ marginBottom: "1em" }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="名前で検索"
            />
          </div>
        </>
      )}

      {userRole !== 9 && error && <p style={{ color: "red" }}>{error}</p>}
      {loading && <p style={{ color: "#555", fontStyle: "italic" }}>データ取得中です...</p>}

      {filteredResults.length > 0 && (
        <table className="result-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("name")}>名前</th>
              <th onClick={() => handleSort("updated")}>最終更新日</th>
              <th>興味分野</th>
              <th>GATB</th>
              <th>性格診断</th>
              <th>コンサルタント</th>
            </tr>
          </thead>
          <tbody>
            {filteredResults.map((r, idx) => (
              <tr key={idx}>
                <td>{r.name}</td>
                <td>{r.updated.toLocaleDateString("ja-JP")}</td>
                {["calling", "GATB", "personal", "consultant"].map((type) => (
                  <td key={type}>
                    {r.signedUrls[type] ? (
                      <a href={r.signedUrls[type]} target="_blank" rel="noopener noreferrer">
                        表示する
                      </a>
                    ) : (
                      <span style={{ color: "#888" }}>未実施</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Personality;
